// Client-side image downscaler. Two-stage compression:
//   1. Resize the long edge to maxEdge (2000 px keeps OCR + handwriting
//      legible while shaving most of a raw phone photo's bytes).
//   2. Iteratively drop JPEG quality (and, if still over budget, dimensions)
//      until the encoded blob is at or below `targetBytes`. Default budget
//      sits at ~900 KB so a stack of pages stays well under Gemini's inline-
//      image cap and a single answer photo loads fast on the teacher's
//      review screen.
//
// On any failure path (HEIC on Chromium, missing canvas context, output
// larger than input) we return the original File — never lose a user's
// upload to a downscale bug.

const DEFAULT_MAX_EDGE     = 2000;
const DEFAULT_TARGET_BYTES = 900 * 1024;   // aim for ≤ ~900 KB
const SOFT_FLOOR_BYTES     = 500 * 1024;   // already small → pass straight through
const MIN_QUALITY          = 0.55;
const MIN_LONG_EDGE        = 1200;

export interface DownscaleOpts {
  maxEdge?: number;        // long-edge pixel limit after resize
  quality?: number;        // initial JPEG quality (0..1)
  targetBytes?: number;    // byte budget; the loop pushes quality/dimensions down to MIN_*
}

export async function downscaleImage(file: File, opts: DownscaleOpts = {}): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  const targetBytes = opts.targetBytes ?? DEFAULT_TARGET_BYTES;
  // Don't bother re-encoding files that are already under the comfort floor —
  // the lossy round-trip can otherwise grow them.
  if (file.size <= SOFT_FLOOR_BYTES) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  try {
    let edge    = Math.min(opts.maxEdge ?? DEFAULT_MAX_EDGE, Math.max(bitmap.width, bitmap.height));
    let quality = opts.quality ?? 0.85;
    let best: Blob | null = null;

    // Up to 6 passes: lower JPEG quality first (cheap, text-friendly), then
    // shrink dimensions if we're still over budget. Stops when we hit either
    // the byte target or the minimum quality + dimension floor.
    for (let pass = 0; pass < 6; pass++) {
      const longest = Math.max(bitmap.width, bitmap.height);
      const scale = edge / longest;
      const w = Math.max(1, Math.round(bitmap.width  * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return file;
      ctx.drawImage(bitmap, 0, 0, w, h);

      const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/jpeg", quality),
      );
      if (!blob) break;
      best = blob;
      if (blob.size <= targetBytes) break;

      if (quality > MIN_QUALITY) {
        quality = Math.max(MIN_QUALITY, quality - 0.1);
      } else if (edge > MIN_LONG_EDGE) {
        edge = Math.max(MIN_LONG_EDGE, edge - 200);
      } else {
        break;
      }
    }

    if (!best) return file;
    if (best.size >= file.size) return file;

    const baseName = file.name.replace(/\.[^.]+$/, "") || "photo";
    return new File([best], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } finally {
    bitmap.close?.();
  }
}

// Convenience: downscale a batch in parallel, preserving order.
export function downscaleImages(files: File[], opts?: DownscaleOpts): Promise<File[]> {
  return Promise.all(files.map((f) => downscaleImage(f, opts)));
}
